<template>
  <Header
    v-if="isFromApp"
    :screenTitle="t('PAYJOY_CARD_VIEWS.SHIPPING_FORM_TITLE_2')"
    :navbarType="'goBack'"
    :showProgressBar="true"
    :progresPercentage="progressPercentage"
    @clicked="handleBackClick"
  />
  <HeaderBanner
    v-if="isFromApp"
    class="w-100"
    :headerBannerImage="useAsset('img/credit-line/cc_data_banner.png')"
    :headerBannerTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_TITLE')"
    :headerBannerSubtitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_SUBTITLE')"
  />
  <div
    class="container px-3 pt-1"
    :class="{ 'keyboard-open': isKeyboardOpen }"
    ref="formContainer"
  >
    <ShipmentsHeader
      :shippingHeaderImage="setShippingHeaderImage"
      :shippingHeaderTitle="setShippingHeaderTitle"
    />

    <!-- AUTOCOMPLETE STEP -->
    <AddressAutocompleteStep
      v-if="currentShipmentStep === 'AUTOCOMPLETE'"
      :autocompleteInput="autocompleteInput"
      :selectOptions="selectOptions"
      :isZipcodeProvider="shouldUseZipcodeAutocomplete"
      :zipcodeError="zipcodeError"
      @onAddressInput="onAddressInput"
      @handleAutocompleteSelect="handleAutocompleteSelect"
      @goToManualForm="goToManualForm"
    />

    <!-- MANUAL STEP -->
    <ManualAddressForm
      v-else-if="currentShipmentStep === 'MANUAL'"
      :shipmentFieldsConfiguration="shipmentFormsConfiguration"
      :isFromApp="isFromApp"
      :deviceTag="deviceTag"
      :streetName="streetName"
      :postalCodeField="postalCodeField"
      :neighborhoodField="neighborhoodField"
      :cityField="cityField"
      :regionField="regionField"
      :citiesCoverage="citiesCoverage"
      :shouldUseCityComposable="shouldUseCityComposable"
      :shouldUseZipcodeAutocomplete="shouldUseZipcodeAutocomplete"
      @goToConfirmClick="onGoToConfirmClick"
      @enterNewAddress="enterNewAddress"
    />

    <!-- CONFIRM ADDRESS STEP -->
    <ConfirmAddressStep
      v-else-if="currentShipmentStep === 'CONFIRM_ADDRESS'"
      :street="streetName"
      :apartment="apartmentField"
      :combinedCityRegion="combinedCityRegion"
      :neighborhood="neighborhoodField"
      :notes="notesField"
      :postalCode="postalCodeField"
      :region="regionField"
      :cityError="cityError"
      :cityValidationPassed="cityValidationPassed"
      :isKeyboardOpen="isKeyboardOpen"
      :disableButton="disableButton"
      :isFromApp="isFromApp"
      :confirmStepConfig="shipmentFieldsConfiguration?.confirmStep"
      @goToManualForm="goToManualForm"
      @submit="handleSubmit"
    />
  </div>

  <DownUpModal
    v-if="typeof isModalOpen === 'boolean' && isModalOpen"
    class="down-up-modal"
    :modalHeaderImage="useAsset('img/credit-line/credit-card/newShipment.png')"
    :modalHeaderTitle="
      t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_TITLE')
    "
    :modalHeaderSubtitle="
      t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_SUBTITLE')
    "
    :btnText="t('PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR_MODAL_BUTTON')"
    @modalAction="enterNewAddress"
  />
</template>

<script setup lang="ts">
import ConfirmAddressStep from "@/apps/credit-line/components/CreditCard/ShippingAddress/ConfirmAddressStep.vue";
import ManualAddressForm from "@/apps/credit-line/components/CreditCard/ShippingAddress/ManualAddressForm.vue";
import ShipmentsHeader from "@/apps/credit-line/components/CreditCard/ShipmentsHeader.vue";
import AddressAutocompleteStep from "@/apps/credit-line/components/CreditCard/ShippingAddress/AddressAutocompleteStep.vue";
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  computed,
  defineEmits,
  defineProps,
  nextTick,
  inject,
} from "vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Header from "@/shared-components/consumer/Header.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import HeaderBanner from "@/apps/credit-line/components/HeaderBanner.vue";
import { NewFormSource } from "./types";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import { useAutocomplete } from "@/apps/credit-line/hooks/useAutocomplete";
import { useZipcodeAutocomplete } from "@/apps/credit-line/hooks/useZipcodeAutocomplete";
import { Service } from "@/apps/credit-line/services/network.service";
import type { ShipmentFieldsConfiguration } from "@/apps/credit-line/types/shipment-config.types";

const { t } = useI18n();
const apartmentField = ref("");
const regionField = ref("");
const notesField = ref("");
const combinedCityRegion = ref("");
const progressPercentage = ref<number>(35);
const shippingAttemptId = ref<string | null>(null);
const isGoogleAutocompleteShown = ref<boolean>(true);
const isKeyboardOpen = ref<boolean>(false);
const isProposedAddressSelected = ref<boolean>(false);
const cityError = ref<string | null>(null);
const cityValidationPassed = ref<boolean>(false);
const isGoogleAutocompleteEnabled = inject("isGoogleAutocompleteEnabled");
const deviceTag = inject("deviceTag");
const creditLineId = inject("creditLineId");
const shipmentFieldsConfiguration = inject<ShipmentFieldsConfiguration>(
  "shipmentFieldsConfiguration"
);
const autocompleteProvider = inject("autocompleteProvider");
const autocompleteCountryCode = inject("autocompleteCountryCode", ref("MX"));

// Computed properties based on configuration
const shouldUseAutocomplete = computed(() => {
  return (
    shipmentFieldsConfiguration.value?.autocomplete?.enabled &&
    shipmentFieldsConfiguration.value?.autocomplete?.provider !== "none"
  );
});

const shouldUseZipcodeAutocomplete = computed(() => {
  return (
    shipmentFieldsConfiguration.value?.autocomplete?.provider === "zipcode" &&
    shipmentFieldsConfiguration.value?.autocomplete?.zipcodeApiEnabled
  );
});

const shouldUseCityComposable = computed(() => {
  return (
    shipmentFieldsConfiguration.value?.validation?.useCityComposable ?? true
  );
});

const shipmentFormsConfiguration = computed(() => {
  const validationRules =
    shipmentFieldsConfiguration.value?.validationRules || {};

  // Convertir validationRules (objeto) a un array de campos para el formulario
  const fieldsArray = Object.entries(validationRules)
    .filter(([_, config]) => config && typeof config === "object")
    .map(([fieldName, config]: [string, Record<string, unknown>]) => ({
      ...config,
      // Asegurar que el name del campo sea consistente
      name: config.name || fieldName,
    }));

  return fieldsArray;
});

const addressScore = ref<string>("");
const isModalOpen = ref<boolean>(false);
const streetName = ref<string>("");
const postalCodeField = ref<string>("");
const neighborhoodField = ref<string>("");
const cityField = ref<string>("");
const disableButton = ref<boolean>(false);
const CreditLineService = new Service();
const citiesCoverage = ref<
  Array<{ city: string; region: string; isCovered: string }>
>([]);

const {
  initProvider,
  selectOptions,
  autocompleteInput,
  components,
  onAddressInput: onAddressInputFromHook,
} = useAutocomplete(autocompleteProvider.value, autocompleteCountryCode.value);

// Hook para zipcode autocomplete (MX)
const { fetchAddressByZipcode, zipcodeError: zipcodeError } =
  useZipcodeAutocomplete(deviceTag.value);

// Handler personalizado para input de autocomplete
const onAddressInput = (value: string | Event) => {
  const inputValue =
    typeof value === "string"
      ? value
      : (value.target as HTMLInputElement)?.value;

  // Actualizar el valor del input
  autocompleteInput.value = inputValue;

  zipcodeError.value = null;

  // Si es provider normal (sitiData, googlePlaces), llamar al hook original
  if (autocompleteProvider.value === "sitiData") {
    onAddressInputFromHook(inputValue);
  }
};

interface Props {
  source?: NewFormSource;
}

const props = withDefaults(defineProps<Props>(), {
  source: NewFormSource.Application,
});

const isFromApp = props.source === NewFormSource.Application;

const handleSubmit = () => {
  disableButton.value = true;
  emit("onContinueClick", {
    street: streetName.value + " " + apartmentField.value,
    neighborhood: neighborhoodField.value,
    city: cityField.value,
    region: regionField.value,
    postalCode: postalCodeField.value === "" ? "000000" : postalCodeField.value,
    apartment: "NA",
    streetNumber: "-",
    notes: notesField.value === "" ? "NA" : notesField.value,
    floor: "NA",
    isZipCodeUserProvided: false,
    source: props.source,
    isProposedAddressSelected: isProposedAddressSelected.value,
    shippingAttemptId: shippingAttemptId.value,
    isGoogleAutocompleteShown: isGoogleAutocompleteShown.value,
    autocompleteProvider: autocompleteProvider.value,
    addressScore: addressScore.value,
  });

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AutoCompleteProviderUsed,
    "CC_Shipments",
    undefined,
    {
      provider: autocompleteProvider.value,
      score: addressScore.value,
      address:
        streetName.value +
        " " +
        apartmentField.value +
        ", " +
        neighborhoodField.value +
        ", " +
        cityField.value +
        ", " +
        regionField.value +
        ", " +
        (postalCodeField.value === "" ? "000000" : postalCodeField.value),
    }
  );
};

const getCoverageZones = async () => {
  try {
    const response = await CreditLineService.getShippingCoverage({
      deviceTag: deviceTag.value,
    });

    citiesCoverage.value = response.data.cities;
  } catch (error) {
    console.error("Error fetching coverage zones:", error);
  }
};

const emit = defineEmits<{
  (e: "onContinueClick", data: unknown): void;
  (e: "goBackClick", data: unknown): void;
}>();

const currentShipmentStep = ref<"AUTOCOMPLETE" | "MANUAL" | "CONFIRM_ADDRESS">(
  "AUTOCOMPLETE"
);

const formContainer = ref<HTMLElement | null>(null);

const setShippingHeaderImage = computed(() => {
  const step = currentShipmentStep.value;
  if (step === "CONFIRM_ADDRESS") {
    return useAsset("img/credit-line/credit-card/confirm_address.png");
  }
  return useAsset("img/credit-line/credit-card/shipments.png");
});

const setShippingHeaderTitle = computed(() => {
  const step = currentShipmentStep.value;
  if (step === "CONFIRM_ADDRESS") {
    return t("PAYJOY_CARD_VIEWS.SHIPPING_FORM_HEADER_CONFIRM");
  }
  return t("PAYJOY_CARD_VIEWS.SHIPPING_FORM_HEADER_DELIVERY");
});

const goToManualForm = () => {
  currentShipmentStep.value = "MANUAL";
};

const onGoToConfirmClick = async (addressData) => {
  streetName.value = addressData.street || "";
  apartmentField.value = addressData.apartment || "";
  cityField.value = addressData.city || "";
  regionField.value = addressData.region || "";
  postalCodeField.value = addressData.postalCode || "";
  neighborhoodField.value = addressData.neighborhood || "";
  notesField.value = addressData.notes || "";

  // For MX, show city and region separately
  if (shouldUseCityComposable.value === false) {
    combinedCityRegion.value = addressData.city || "";
  } else {
    // For CO/PE, combine city and region
    combinedCityRegion.value =
      addressData.city && addressData.region
        ? `${addressData.city} (${addressData.region})`
        : addressData.city || "";
  }

  // Advance to the final step
  currentShipmentStep.value = "CONFIRM_ADDRESS";
  progressPercentage.value = 100;
  cityValidationPassed.value = true;
};

const handleBackClick = () => {
  if (currentShipmentStep.value === "CONFIRM_ADDRESS") {
    currentShipmentStep.value = "MANUAL";
    progressPercentage.value = 66;
  } else if (
    currentShipmentStep.value === "MANUAL" &&
    !isGoogleAutocompleteEnabled.value
  ) {
    currentShipmentStep.value = "AUTOCOMPLETE";
    initProvider();
    progressPercentage.value = 35;
    autocompleteInput.value = "";
    selectOptions.value = [];
    isProposedAddressSelected.value = true;

    streetName.value = "";
    postalCodeField.value = "";
    neighborhoodField.value = "";
    cityField.value = "";
    regionField.value = "";
    notesField.value = "";
  } else {
    emit("goBackClick", {});
  }
};

const enterNewAddress = () => {
  isModalOpen.value = false;
};

const assignValuesFromAutocomplete = async (newComponents) => {
  postalCodeField.value = newComponents.postalCode || "";
  // Street: use street and streetNumber if they exist
  streetName.value = [newComponents.street, newComponents.streetNumber]
    .filter(Boolean)
    .join(" ")
    .trim();
  neighborhoodField.value = newComponents.neighborhood || "";
  // Combined city and region
  cityField.value = (newComponents.city || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  combinedCityRegion.value = cityField.value;
  await nextTick(); // Force render so that manual form receives updated values
};

// --- GOOGLE AUTOCOMPLETE WATCHER ---
watch(
  () => components,
  async (newComponents) => {
    // Google Places Autocomplete selected an address
    if (autocompleteProvider.value === "googlePlaces") {
      await assignValuesFromAutocomplete(newComponents);
      await nextTick();
      isProposedAddressSelected.value = true;
      currentShipmentStep.value = "MANUAL";
      cityValidationPassed.value = true;
      cityError.value = null;
    }
  },
  { deep: true }
);

// --- ZIPCODE AUTOCOMPLETE WATCHER (MX) ---
watch(
  () => autocompleteInput.value,
  async (newZipcode) => {
    // Only for zipcode provider (MX)
    if (shouldUseZipcodeAutocomplete.value && newZipcode?.length === 5) {
      try {
        const addressData = await fetchAddressByZipcode(newZipcode);
        if (addressData) {
          // Check if the zone has coverage
          if (!addressData.isZoneWithCoverage) {
            // Set error to show it in the input
            zipcodeError.value = "PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE";
            return;
          }

          // Clear error if there was a previous one
          zipcodeError.value = null;

          // Pre-fill the fields with zipcode data
          postalCodeField.value = newZipcode; // Use the entered zipcode
          cityField.value = addressData.city;
          regionField.value = addressData.region;
          // neighborhoodField can be left empty since the backend doesn't return it yet

          await nextTick();

          // Automatically go to the manual form with pre-filled data
          isProposedAddressSelected.value = true;
          currentShipmentStep.value = "MANUAL";
          progressPercentage.value = 66;
          cityValidationPassed.value = true;
          cityError.value = null;
        }
      } catch (error) {
        // Error in zipcode autocomplete
      }
    }
  }
);

onMounted(async () => {
  getCoverageZones();

  // Determine initial step based on configuration
  if (shouldUseZipcodeAutocomplete.value) {
    // For MX with zipcode autocomplete: SHOW autocomplete step (to request postal code)
    currentShipmentStep.value = "AUTOCOMPLETE";
    progressPercentage.value = 35;
    isGoogleAutocompleteShown.value = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.GoogleAutocompleteShown,
      "CC_Details"
    );
  } else if (
    !shouldUseAutocomplete.value ||
    isGoogleAutocompleteEnabled.value
  ) {
    // No autocomplete or autocomplete disabled
    currentShipmentStep.value = "MANUAL";
    isGoogleAutocompleteShown.value = false;
    progressPercentage.value = 66;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.GoogleAutocompleteNotShown,
      "CC_Details"
    );
  } else {
    // With Google/other autocomplete provider
    currentShipmentStep.value = "AUTOCOMPLETE";
    progressPercentage.value = 35;
    isGoogleAutocompleteShown.value = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.GoogleAutocompleteShown,
      "CC_Details"
    );
  }

  await initProvider();
  shippingAttemptId.value = "NEW" + (creditLineId?.value ?? "") + Date.now();

  const handleResize = () => {
    isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
  };

  window.addEventListener("resize", handleResize);
  handleResize();
});

onUnmounted(() => {
  const handleResize = () => {
    isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
  };
  window.removeEventListener("resize", handleResize);
});

const handleAutocompleteSelect = async (option) => {
  streetName.value = option.original.direccion || "";
  neighborhoodField.value = option.original.barrio || "";
  cityField.value = option.original.ciudad
    ? option.original.ciudad.toUpperCase()
    : "";
  regionField.value = option.original.departamento || "";
  addressScore.value = option.original.score || "";
  await nextTick();
  isProposedAddressSelected.value = true;

  autocompleteInput.value = "";
  selectOptions.value = [];

  currentShipmentStep.value = "MANUAL";
  cityValidationPassed.value = true;
  cityError.value = null;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.form-container {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  transition: padding-bottom 0.3s ease;
}

.container {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  transition: padding-bottom 0.3s ease;
}

.container.is-modal-open {
  filter: blur(4px);
  pointer-events: none;
}

@keyframes bounce-in-right {
  0% {
    opacity: 0;
    transform: translateY(2000px);
  }
  40% {
    opacity: 1;
    transform: translateY(-30px);
  }
  80% {
    transform: translateY(10px);
  }
  100% {
    transform: translateY(0);
  }
}

.container.keyboard-open {
  padding-bottom: 150px;
}
</style>
